import { CopyToClipboard } from 'react-copy-to-clipboard';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { IntegrationBusinessUnit } from 'api';

import { Badge } from '@atoms/badges/Badge/Badge';
import { CopyIcon } from '@atoms/Icon/CopyIcon';

import { BusinessUnitCardActions } from './CardActions';
import {
  BUSINESS_UNIT_CARD_COPY_UUID_TEST_ID,
  BUSINESS_UNIT_CARD_TEST_ID,
  BUSINESS_UNIT_DEFAULT_BADGE_LABEL,
  BUSINESS_UNIT_DEFAULT_BADGE_TEST_ID,
  BUSINESS_UNIT_UUID_FIELD_LABEL
} from './constants';

type BusinessUnitCardProps = {
  businessUnit: IntegrationBusinessUnit;
};

export const BusinessUnitCard = ({ businessUnit }: BusinessUnitCardProps) => {
  const openNotification = useOpenNotification();

  const onCopySuccess = (label: string) =>
    openNotification({
      kind: 'success',
      message: `${label} copied to your clipboard`
    });

  return (
    <div
      data-testid={BUSINESS_UNIT_CARD_TEST_ID(businessUnit.uuid)}
      className="flex w-[363px] max-w-full flex-col gap-3 rounded-lg border border-border-default bg-white p-4"
    >
      <div className="flex items-center gap-4 pb-4">
        <p className="flex-1 text-base font-medium leading-[22px] text-content-primary">
          {businessUnit.name}
        </p>
        {businessUnit.default && (
          <Badge
            label={BUSINESS_UNIT_DEFAULT_BADGE_LABEL}
            data-testid={BUSINESS_UNIT_DEFAULT_BADGE_TEST_ID(businessUnit.uuid)}
            className="bg-tenant-subtle text-tenant-primary"
          />
        )}
        <BusinessUnitCardActions businessUnit={businessUnit} />
      </div>

      <div className="flex flex-col gap-2 p-1.5">
        <p className="text-xs font-semibold leading-[14px] text-content-primary">
          {BUSINESS_UNIT_UUID_FIELD_LABEL}
        </p>
        <div className="flex items-center justify-between rounded-lg bg-surface-muted p-2">
          <span className="font-mono text-xs leading-[22px] tracking-[0.36px] text-content-primary">
            {businessUnit.uuid}
          </span>
          <CopyToClipboard
            text={businessUnit.uuid}
            onCopy={() => onCopySuccess(BUSINESS_UNIT_UUID_FIELD_LABEL)}
          >
            <button
              type="button"
              data-testid={BUSINESS_UNIT_CARD_COPY_UUID_TEST_ID(
                businessUnit.uuid
              )}
              aria-label={`Copy ${BUSINESS_UNIT_UUID_FIELD_LABEL}`}
              className="cursor-pointer text-content-secondary"
            >
              <CopyIcon
                width={20}
                height={20}
                stroke="auto"
                className="shrink-0"
              />
            </button>
          </CopyToClipboard>
        </div>
      </div>
    </div>
  );
};
